import _jsx from "@babel/runtime/helpers/jsx";
/* eslint-disable react/prop-types */

import activeElement from 'dom-helpers/activeElement';
import contains from 'dom-helpers/query/contains';
import canUseDom from 'dom-helpers/util/inDOM';
import PropTypes from 'prop-types';
import componentOrElement from 'prop-types-extra/lib/componentOrElement';
import deprecated from 'prop-types-extra/lib/deprecated';
import elementType from 'prop-types-extra/lib/elementType';
import React, { cloneElement } from 'react';
import ReactDOM from 'react-dom';
import warning from 'warning';
import ModalManager from './ModalManager';
import Portal from './Portal';
import RefHolder from './RefHolder';
import addEventListener from './utils/addEventListener';
import addFocusListener from './utils/addFocusListener';
import getContainer from './utils/getContainer';
import ownerDocument from './utils/ownerDocument';
const modalManager = new ModalManager();

/**
 * Love them or hate them, `<Modal/>` provides a solid foundation for creating dialogs, lightboxes, or whatever else.
 * The Modal component renders its `children` node in front of a backdrop component.
 *
 * The Modal offers a few helpful features over using just a `<Portal/>` component and some styles:
 *
 * - Manages dialog stacking when one-at-a-time just isn't enough.
 * - Creates a backdrop, for disabling interaction below the modal.
 * - It properly manages focus; moving to the modal content, and keeping it there until the modal is closed.
 * - It disables scrolling of the page content while open.
 * - Adds the appropriate ARIA roles are automatically.
 * - Easily pluggable animations via a `<Transition/>` component.
 *
 * Note that, in the same way the backdrop element prevents users from clicking or interacting
 * with the page content underneath the Modal, Screen readers also need to be signaled to not to
 * interact with page content while the Modal is open. To do this, we use a common technique of applying
 * the `aria-hidden='true'` attribute to the non-Modal elements in the Modal `container`. This means that for
 * a Modal to be truly modal, it should have a `container` that is _outside_ your app's
 * React hierarchy (such as the default: document.body).
 */
class Modal extends React.Component {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    this.renderBackdrop = () => {
      const {
        backdropStyle,
        backdropClassName,
        renderBackdrop,
        backdropTransition: Transition
      } = this.props;
      const backdropRef = ref => {
        this.backdrop = ref;
      };
      let backdrop = renderBackdrop({
        ref: backdropRef,
        style: backdropStyle,
        className: backdropClassName,
        onClick: this.handleBackdropClick
      });
      if (Transition) {
        backdrop = /*#__PURE__*/_jsx(Transition, {
          appear: true,
          in: this.props.show
        }, void 0, backdrop);
      }
      return backdrop;
    };
    this.onPortalRendered = () => {
      this.autoFocus();
      if (this.props.onShow) {
        this.props.onShow();
      }
    };
    this.onShow = () => {
      const doc = ownerDocument(this);
      const container = getContainer(this.props.container, doc.body);
      this.props.manager.add(this, container, this.props.containerClassName);
      this._onDocumentKeydownListener = addEventListener(doc, 'keydown', this.handleDocumentKeyDown);
      this._onDocumentKeyupListener = addEventListener(doc, 'keyup', this.handleDocumentKeyUp);
      this._onFocusinListener = addFocusListener(this.enforceFocus);
    };
    this.onHide = () => {
      this.props.manager.remove(this);
      this._onDocumentKeydownListener.remove();
      this._onDocumentKeyupListener.remove();
      this._onFocusinListener.remove();
      if (this.props.restoreFocus) {
        this.restoreLastFocus();
      }
    };
    this.setMountNode = ref => {
      this.mountNode = ref ? ref.getMountNode() : ref;
    };
    this.setModalNodeRef = ref => {
      this.modalNode = ref;
    };
    this.setDialogRef = ref => {
      this.dialog = ref;
    };
    this.handleHidden = function () {
      _this.setState({
        exited: true
      });
      _this.onHide();
      if (_this.props.onExited) {
        _this.props.onExited(...arguments);
      }
    };
    this.handleBackdropClick = e => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (this.props.onBackdropClick) {
        this.props.onBackdropClick(e);
      }
      if (this.props.backdrop === true) {
        this.props.onHide();
      }
    };
    this.handleDocumentKeyDown = e => {
      if (this.props.keyboard && e.keyCode === 27 && this.isTopModal()) {
        if (this.props.onEscapeKeyDown) {
          this.props.onEscapeKeyDown(e);
        }
        this.props.onHide();
      }
    };
    this.handleDocumentKeyUp = e => {
      if (this.props.keyboard && e.keyCode === 27 && this.isTopModal()) {
        if (this.props.onEscapeKeyUp) {
          this.props.onEscapeKeyUp(e);
        }
      }
    };
    this.checkForFocus = () => {
      if (canUseDom) {
        this.lastFocus = activeElement();
      }
    };
    this.enforceFocus = () => {
      if (!this.props.enforceFocus || !this._isMounted || !this.isTopModal()) {
        return;
      }
      const dialogElement = this.getDialogElement();
      const currentActiveElement = activeElement(ownerDocument(this));
      if (dialogElement && !contains(dialogElement, currentActiveElement)) {
        dialogElement.focus();
      }
    };
    this.state = {
      exited: !props.show
    };
  }
  omitProps(props, propTypes) {
    // List of props that should not be passed to DOM elements
    const modalSpecificProps = ['show', 'container', 'onShow', 'renderBackdrop', 'onEscapeKeyDown', 'onEscapeKeyUp', 'onBackdropClick', 'backdropStyle', 'backdropClassName', 'containerClassName', 'keyboard', 'transition', 'backdropTransition', 'enforceFocus', 'restoreFocus', 'onEnter', 'onEntering', 'onEntered', 'onExit', 'onExiting', 'onExited', 'onHide', 'backdrop', 'manager', 'autoFocus'];
    const keys = Object.keys(props);
    const newProps = {};
    keys.forEach(prop => {
      // Skip modal-specific props
      if (modalSpecificProps.includes(prop)) {
        return;
      }

      // Skip props defined in propTypes if provided
      if (propTypes && Object.prototype.hasOwnProperty.call(propTypes, prop)) {
        return;
      }
      newProps[prop] = props[prop];
    });
    return newProps;
  }
  render() {
    const {
      show,
      container,
      children,
      transition: Transition,
      backdrop,
      className,
      style,
      onExit,
      onExiting,
      onEnter,
      onEntering,
      onEntered
    } = this.props;
    let dialog = React.Children.only(children);
    const filteredProps = this.omitProps(this.props, Modal.propTypes);
    const mountModal = show || Transition && !this.state.exited;
    if (!mountModal) {
      return null;
    }
    const {
      role,
      tabIndex
    } = dialog.props;
    if (role === undefined || tabIndex === undefined) {
      dialog = /*#__PURE__*/cloneElement(dialog, {
        role: role === undefined ? 'document' : role,
        tabIndex: tabIndex == null ? '-1' : tabIndex
      });
    }
    if (Transition) {
      dialog = /*#__PURE__*/_jsx(Transition, {
        appear: true,
        unmountOnExit: true,
        in: show,
        onExit: onExit,
        onExiting: onExiting,
        onExited: this.handleHidden,
        onEnter: onEnter,
        onEntering: onEntering,
        onEntered: onEntered
      }, void 0, dialog);
    }
    return /*#__PURE__*/React.createElement(Portal, {
      ref: this.setMountNode,
      container: container,
      onRendered: this.onPortalRendered
    }, /*#__PURE__*/React.createElement("div", Object.assign({
      ref: this.setModalNodeRef,
      role: role || 'dialog'
    }, filteredProps, {
      style: style,
      className: className
    }), backdrop && this.renderBackdrop(), /*#__PURE__*/React.createElement(RefHolder, {
      ref: this.setDialogRef
    }, dialog)));
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.show) {
      this.setState({
        exited: false
      });
    } else if (!nextProps.transition) {
      // Otherwise let handleHidden take care of marking exited.
      this.setState({
        exited: true
      });
    }
  }
  UNSAFE_componentWillUpdate(nextProps) {
    if (!this.props.show && nextProps.show) {
      this.checkForFocus();
    }
  }
  componentDidMount() {
    this._isMounted = true;
    if (this.props.show) {
      this.onShow();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      transition
    } = this.props;
    if (prevProps.show && !this.props.show && !transition) {
      // Otherwise handleHidden will call this.
      this.onHide();
    } else if (!prevProps.show && this.props.show) {
      this.onShow();
    }
  }
  componentWillUnmount() {
    const {
      show,
      transition
    } = this.props;
    this._isMounted = false;
    if (show || transition && !this.state.exited) {
      this.onHide();
    }
  }
  autoFocus() {
    if (!this.props.autoFocus) {
      return;
    }
    const dialogElement = this.getDialogElement();
    const currentActiveElement = activeElement(ownerDocument(this));
    if (dialogElement && !contains(dialogElement, currentActiveElement)) {
      this.lastFocus = currentActiveElement;
      if (!dialogElement.hasAttribute('tabIndex')) {
        process.env.NODE_ENV !== "production" ? warning(false, 'The modal content node does not accept focus. For the benefit of ' + 'assistive technologies, the tabIndex of the node is being set ' + 'to "-1".') : void 0;
        dialogElement.setAttribute('tabIndex', -1);
      }
      dialogElement.focus();
    }
  }
  restoreLastFocus() {
    // Support: <=IE11 doesn't support `focus()` on svg elements (RB: #917)
    if (this.lastFocus && this.lastFocus.focus) {
      this.lastFocus.focus();
      this.lastFocus = null;
    }
  }
  getDialogElement() {
    return ReactDOM.findDOMNode(this.dialog);
  }
  isTopModal() {
    return this.props.manager.isTopModal(this);
  }
}
Modal.defaultProps = {
  show: false,
  backdrop: true,
  keyboard: true,
  autoFocus: true,
  enforceFocus: true,
  restoreFocus: true,
  onHide: () => {},
  manager: modalManager,
  renderBackdrop: props => /*#__PURE__*/React.createElement("div", props)
};
Modal.Manager = ModalManager;
export default Modal;